import React, { useState, useContext, useEffect, useReducer } from 'react';
import { AsButton, AsDatePicker, LabelledTickbox, useModal } from '@adserve/adserve-react-components';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import SpotsGrid from './SpotsGrid';
import {
    DownloadBurstTransmissionTimesPdf,
    DownloadTransmissionTimesPdf,
    GetCampaignBurstSpots,
    GetCampaignBurstVersionSpots,
    GetCampaignSpots,
    GetCampaignVersionSpots
} from '../../../services/CampaignsDataService';
import AsDropdown from '../OffAirCosts/OffAirDropdown/Dropdown'; // ToDo: move the dropdown component to common location
import { apiParamsReducer } from '../../../services/AsGridService';
import moment from 'moment';

const labelStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 12
};

const buttonStyle = {
    width: 'max-content',
    padding: '0 15px',
    fontSize: 14,
    fontWeight: 'bold'
};

const flexRow = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20
};

export const inputStyle = {
    boxSizing: 'border-box',
    width: '100%',
    height: 40,
    outline: 'none',
    autoComplete: 'off',
    padding: '0 12px',
    fontSize: 15,
    letterSpacing: -0.3,
    fontWeight: 600
};

const SpotsSearchPanel = ({
    campaignId,
    campaignBurstId,
    transmitterList,
    readOnly,
    campaignStartDate,
    campaignEndDate,
    currentCampaignName,
    campaignVersionId
}) => {
    const { addMessage } = useModal();
    const { dateFormat } = useContext(SystemSettingsContext);
    const [inProgress, setInProgress] = useState(false);
    const [transmitterId, setTransmitterId] = useState();
    const [fromDate, setFromDate] = useState(moment(campaignStartDate).utc(true).startOf('day'));
    const [toDate, setToDate] = useState(moment(campaignEndDate).utc(true).startOf('day'));
    const [data, setData] = useState([]);
    const [hidePlayoutLogs, setHidePlayoutLogs] = useState(true);

    // ASSP-7411 Defined sortFields parameter to specify an initial sorting by station, date and time - 
    // overrideStartTime is a proxy for daypart and used here to give a neater order when breakTime is 
    // set to 0 because the breakId is null - overrideEndTime might be useful
    const apiParamDefaults = {
        // sortField: 'breakTime', // ASSP-7411 Unset to show initial endpoint-sorting by station, date and time
        sortFields: 'transmitterName,breakDate,breakTime,overrideStartTime', // ,overrideEndTime', // ASSP-7411 Added 
        sortOrder: 'asc', // ASSP-7411 Originally desc
        pageNumber: 1,
        pageSize: 999
    };

    const [apiParams, apiParamsDispatcher] = useReducer(apiParamsReducer, apiParamDefaults);

    const handleDateChange = (event) => {
        const value = moment(event.target.value).utc(true).startOf('day');
        event.target.id === 'fromDate' ? setFromDate(value) : setToDate(value);
    };

    const handleSelectChange = (name, id) => {
        setTransmitterId(id);
    };

    const handleHidePlayoutClick = () => {
        setHidePlayoutLogs(!hidePlayoutLogs);
    };

    const loadData = async () => {
        setInProgress(true);
        if (campaignBurstId) {
            try {
                const data = campaignVersionId && campaignVersionId !== window.emptyGuid ? await GetCampaignBurstVersionSpots(campaignVersionId, apiParams) : await GetCampaignBurstSpots(campaignBurstId, apiParams);
                setData(data?.data || []);
            } catch (err) {
                addMessage({
                    message: window.translate('An error occurred while attempting to retrieve transmission times.'),
                    buttons: [
                        {
                            text: window.translate('Retry'),
                            callback: loadData,
                            useSecondaryStyle: true
                        },
                        {
                            text: window.translate('Ok'),
                            callback: null
                        }
                    ]
                });
            } finally {
                setInProgress(false);
            }
        } else {
            try {
                const data = campaignVersionId && campaignVersionId !== window.emptyGuid ? await GetCampaignVersionSpots(campaignVersionId, apiParams) : await GetCampaignSpots(campaignId, apiParams);
                // console.log('SpotsSearchPanel loadData', data?.data)
                setData(data?.data || []);
            } catch (err) {
                addMessage({
                    message: window.translate('An error occurred while attempting to retrieve transmission times.'),
                    buttons: [
                        {
                            text: window.translate('Retry'),
                            callback: loadData,
                            useSecondaryStyle: true
                        },
                        {
                            text: window.translate('Ok'),
                            callback: null
                        }
                    ]
                });
            } finally {
                setInProgress(false);
            }
        }
    };

    const onDownloadPdfClick = async () => {
        setInProgress(true);
        try {
            const base64Pdf = campaignBurstId ? await DownloadBurstTransmissionTimesPdf({ ...apiParams, campaignId, campaignBurstId: campaignBurstId }) : await DownloadTransmissionTimesPdf({ ...apiParams, campaignId });
            const newTab = window.open();
            newTab.document.title = 'Transmission Times';
            newTab.document.body.style.margin = '0';
            newTab.document.body.innerHTML = `<iframe
                style="width: 100%;height: 100%;box-sizing: border-box;"
                src="data:application/pdf;base64,${base64Pdf}"></iframe>`;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to download pdf.\n${err?.message || err}`);
            addMessage({
                message: window.translate('An error occurred while attempting to download the transmission times PDF.'),
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        } finally {
            setInProgress(false);
        }
    };

    useEffect(() => {
        apiParamsDispatcher({ transmitterId: transmitterId === '-1' ? window.emptyGuid : transmitterId });
    }, [transmitterId]);

    useEffect(() => {
        apiParamsDispatcher({ hidePlayoutLogs });
    }, [hidePlayoutLogs]);

    useEffect(() => {
        apiParamsDispatcher({ fromDate: fromDate.toDate() });
    }, [fromDate]);

    useEffect(() => {
        apiParamsDispatcher({ toDate: toDate.toDate() });
    }, [toDate]);

    useEffect(() => {
        // ASSP-6675 Fix added when investigating campaign LC3120737 to prevent the initial download
        // of 1540 items overwriting the later but quicker download of 1304 items - apiParams is 
        // initialized with sortField, sortOrder, pageNumber & pageSize - the apiParams.property 
        // hidePlayoutLogs is defined when react hidePlayoutLogs definition triggers its useEffect
        //
        if (apiParams?.hidePlayoutLogs !== undefined) {
            loadData();
        }
    }, [apiParams, campaignVersionId]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                margin: '11px 24px 25px'
            }}
        >
            <div style={flexRow}>
                <div>
                    <AsDropdown
                        id='transmitter'
                        name='transmitterId'
                        title={window.translate('Log')}
                        values={[{ id: '-1', name: 'All Logs' }, ...transmitterList]}
                        value={transmitterId || '-1'}
                        onChange={handleSelectChange}
                        style={{ width: 376, zIndex: 220 }}
                        readOnly={readOnly}
                    />
                </div>
                <LabelledTickbox
                    id='hidePlayout'
                    title={window.translate('Hide playout only logs')}
                    reverseElements
                    gap={10}
                    size={18}
                    onClick={handleHidePlayoutClick}
                    value={hidePlayoutLogs}
                    containerStyle={{position: 'absolute', marginLeft: 390, marginTop: 38}}
                ></LabelledTickbox>
                <div style={{ display: 'flex' }}>
                    <div>
                        <label
                            style={{
                                ...labelStyle,
                                paddingLeft: 24
                            }}
                        >
                            {window.translate('Date from')}
                        </label>
                        <div
                            style={{
                                width: 226,
                                marginLeft: 24
                            }}
                        >
                            <AsDatePicker
                                id='fromDate'
                                inputStyle={{ ...inputStyle, width: '80%' }}
                                currentDate={fromDate || window.translate('Select date ...')}
                                onChange={handleDateChange}
                                dateFormat={dateFormat}
                                bodyContainerStyle={{ zIndex: 200 }}
                            />
                        </div>
                    </div>
                    <div>
                        <label
                            style={{
                                ...labelStyle,
                                marginLeft: 20
                            }}
                        >
                            {window.translate('Date to')}
                        </label>
                        <div
                            style={{
                                width: 226,
                                marginLeft: 20
                            }}
                        >
                            <AsDatePicker
                                id='toDate'
                                inputStyle={{ ...inputStyle, width: '80%' }}
                                currentDate={toDate || window.translate('Select date ...')}
                                onChange={handleDateChange}
                                dateFormat={dateFormat}
                                bodyContainerStyle={{ zIndex: 200 }}
                            />
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}>
                    <AsButton id='excel-format' style={buttonStyle} disabled={inProgress}>
                        {window.translate('Download Excel')}
                    </AsButton>
                    <AsButton style={buttonStyle} handleClick={onDownloadPdfClick} disabled={inProgress}>
                        {inProgress ? window.translate('Please wait...') : window.translate('Download PDF')}
                    </AsButton>
                </div>
            </div>
            <SpotsGrid data={data} campaignName={currentCampaignName} />
        </div>
    );
};

export default SpotsSearchPanel;
